import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, PieLabelRenderProps } from 'recharts';
import DeviceStatsModelSelector from './DeviceStatsModelSelector';

// Color palette - keep Other as the last color
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', 
  '#82CA9D', '#8DD1E1', '#A4DE6C', '#D0ED57', '#FFC658',
  '#F66D44', '#FEAE65', '#E6F69D', '#AADEA7', '#64C2A6'
];

// Grey color specifically for "Other" category
const OTHER_COLOR = '#AAAAAA';

interface FirmwareVersionChartProps {
  apiBaseUrl: string;
}

interface VersionDataItem {
  name: string;
  value: number;
  displayName: string;
  sortKey: number[];
  isOther?: boolean;
}

// Helper function to format version with 'v' prefix
const formatVersion = (version: string): string => {
  if (!version) return 'Unknown';
  if (version === 'Other') return 'Other';
  
  // If version already starts with 'v' or 'V', don't add another one
  if (version.match(/^[vV]/)) {
    return version;
  }
  return `v${version}`;
};

// Helper function to parse version string into an array of numbers for sorting
const parseVersionForSorting = (version: string): number[] => {
  if (version === 'Other') return [-1]; // Always sort "Other" last
  
  // Remove 'v' or 'V' prefix if present
  const cleanVersion = version?.replace(/^[vV]/, '') || '';
  
  // Split by dots and convert to numbers
  const parts = cleanVersion.split('.');
  
  // Convert each part to a number, defaulting to 0 if not a valid number
  return parts.map(part => {
    const parsed = parseInt(part, 10);
    return isNaN(parsed) ? 0 : parsed;
  });
};

const FirmwareVersionChart: React.FC<FirmwareVersionChartProps> = ({ apiBaseUrl }) => {
  const [versionData, setVersionData] = useState<VersionDataItem[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalDevices, setTotalDevices] = useState<number>(0);
  const [topVersionCount, setTopVersionCount] = useState<number>(5);

  // Fetch version statistics
  const fetchVersionStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/devices/version-metrics`, {
        withCredentials: true,
        params: {
          type: 'prod', // Only production devices
          models: selectedModels.length > 0 ? selectedModels.join(',') : undefined,
          top: topVersionCount // Number of top versions to show
        }
      });
      
      if (response.data && response.data.versions) {
        const { versions, totalDevices } = response.data;
        
        // Map the versions to our data format
        const formattedData = versions.map((item: any) => ({
          name: item.version || 'Unknown',
          displayName: formatVersion(item.version || 'Unknown'),
          value: item.count,
          sortKey: parseVersionForSorting(item.version || 'Unknown'),
          isOther: item.version === 'Other'
        }));
        
        // Sort by count (descending), but always put "Other" at the end
        formattedData.sort((a: VersionDataItem, b: VersionDataItem) => {
          if (a.isOther) return 1;
          if (b.isOther) return -1;
          return b.value - a.value;
        });
                
        setVersionData(formattedData);
        setTotalDevices(totalDevices);
        setError(null);
      } else {
        throw new Error("Invalid response format from version stats endpoint");
      }
    } catch (error: any) {
      console.error('Error fetching version statistics:', error);
      setError('Failed to load firmware version data');
      setVersionData([]);
      setTotalDevices(0);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchVersionStats();
  }, [apiBaseUrl, selectedModels, topVersionCount]);

  const handleModelSelectionChange = (models: string[]) => {
    setSelectedModels(models);
  };

  const handleTopVersionsChange = (count: number) => {
    setTopVersionCount(count);
  };

  const handleRefresh = () => {
    fetchVersionStats();
  };

  // Fixed render function with proper type handling
  const renderCustomizedLabel = (props: PieLabelRenderProps) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
    
    // Skip rendering if any required value is missing or percent is too small
    if (!percent || percent < 0.05 || typeof cx !== 'number' || typeof cy !== 'number' || 
        typeof innerRadius !== 'number' || typeof outerRadius !== 'number' || 
        typeof midAngle !== 'number') {
      return null;
    }
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Find the most popular version for summary
  const getMostPopularVersion = () => {
    if (versionData.length === 0) return null;
    
    // Find the first non-"Other" version with the highest count
    const nonOtherVersions = versionData.filter(v => !v.isOther);
    return nonOtherVersions.length > 0 ? nonOtherVersions[0] : null;
  };

  // Custom legend content
  const renderCustomLegend = () => {
    return (
      <div>
        {versionData.map((entry, index) => {
          const color = entry.isOther ? OTHER_COLOR : COLORS[index % COLORS.length];
          return (
            <div key={`item-${entry.name}`} className="d-flex align-items-center mb-1">
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: color,
                  marginRight: '5px',
                  borderRadius: '2px'
                }}
              />
              <span style={{ fontSize: '0.9rem' }}>
                {entry.displayName} 
                <span className="text-muted ms-1">
                  {entry.value.toLocaleString()} ({((entry.value/totalDevices)*100).toFixed(1)}%)
                </span>
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const mostPopularVersion = getMostPopularVersion();

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title">Firmware Version Distribution</h5>
        <div className="text-muted">Production devices only</div>
      </div>
      <div className="card-body">
        <div className="mb-3">
          <DeviceStatsModelSelector 
            apiBaseUrl={apiBaseUrl}
            selectedModels={selectedModels}
            onSelectionChange={handleModelSelectionChange}
          />
        </div>
        
        <div className="mb-3">
          {/*<label className="form-label">Top versions to show:</label>*/}
          <div className="btn-group">
            {[3, 5, 10].map(count => (
              <button
                key={count}
                className={`btn btn-sm ${topVersionCount === count ? 'btn-secondary' : 'btn-outline-secondary'}`}
                onClick={() => handleTopVersionsChange(count)}
              >
                Top {count}
              </button>
            ))}
          </div>
        </div>
        
        {loading ? (
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error && versionData.length === 0 ? (
          <div className="alert alert-danger">{error}</div>
        ) : versionData.length === 0 ? (
          <div className="alert alert-info">
            No firmware version data available for the selected models.
          </div>
        ) : (
          <div className="row">
            <div className="col-md-7">
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={versionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    innerRadius={0}
                    outerRadius={130}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="displayName"
                  >
                    {versionData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.isOther ? OTHER_COLOR : COLORS[index % COLORS.length]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number, name: string) => [
                      `${value.toLocaleString()} devices (${((value/totalDevices)*100).toFixed(1)}%)`, 
                      `Firmware ${name}`
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="col-md-5">
              <div className="mb-4">
                <div className="card bg-light">
                  <div className="card-body">
                    <h6 className="card-title">Summary</h6>
                    <div className="mb-2">
                      <strong>Total devices:</strong> {totalDevices.toLocaleString()}
                    </div>
                    <div className="mb-2">
                      <strong>Unique versions:</strong> {versionData.some(v => v.isOther) 
                        ? `${versionData.length - 1}+` // Subtract 1 for "Other" and add + to indicate there are more
                        : versionData.length
                      }
                    </div>
                    {mostPopularVersion && (
                      <div>
                        <strong>Most common:</strong> {mostPopularVersion.displayName} 
                        <span className="text-muted"> ({mostPopularVersion.value.toLocaleString()} devices)</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Simplified legend */}
              <div className="mt-3">
                <h6 className="text-muted mb-2">Firmware Versions</h6>
                {renderCustomLegend()}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="card-footer bg-white d-flex justify-content-between align-items-center">
        <div>
          <small className="text-muted">
            {selectedModels.length === 0 
              ? 'No models selected' 
              : `Showing data for ${selectedModels.length} model${selectedModels.length !== 1 ? 's' : ''}`
            }
          </small>
        </div>
        <button 
          className="btn btn-sm btn-outline-primary"
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>
    </div>
  );
};

export default FirmwareVersionChart;